/** Your account: the Discord account you signed in with, and linking the Showdown name you play as */
import { useEffect, useState } from 'preact/hooks';
import { api, signInUrl } from '../api';
import { refreshMe, signOut, useMe } from '../me';
import { navigate } from '../router';
import { ActionButton, Countdown, toast } from '../ui';

interface LinkCode {
	code: string;
	expiresAt: string;
	command: string;
}

export function AccountPage() {
	const me = useMe();
	const [code, setCode] = useState<LinkCode | null>(null);
	const user = me?.user;
	const linkedId = user?.showdown?.id;

	// while a code is showing, watch for it being used on Showdown
	useEffect(() => {
		if (!code) return;
		const timer = setInterval(() => void refreshMe(), 3000);
		const expire = setTimeout(() => setCode(null), new Date(code.expiresAt).getTime() - Date.now());
		return () => {
			clearInterval(timer);
			clearTimeout(expire);
		};
	}, [code]);
	useEffect(() => {
		if (code && linkedId) {
			setCode(null);
			toast(`Linked! You're ${user!.showdown!.name} in your leagues now.`, 'good');
		}
	}, [linkedId]);

	if (!me) return null;
	if (!user) {
		return <div class="card">
			<h1>Your account</h1>
			<p>Sign in with Discord to play in your leagues and save your plans.</p>
			<a class="button primary" href={signInUrl('/account')}>Sign in with Discord</a>
			<p class="muted small" style="margin-top:12px">Can't use Discord? Ask a Global Admin to let you sign in with your
				Showdown name. Then log in on <a href="https://play.ogreclan.com">play.ogreclan.com</a> as that name, type{' '}
				<code>/ogreclan signin</code> in any chat box, and open the link it gives you.</p>
		</div>;
	}
	return <>
		<div class="card">
			<h1>Your account</h1>
			<p>Signed in with Discord as <strong>{user.discord?.name || user.name}</strong>.</p>
			<ActionButton action={async () => {
				await signOut();
				navigate('/');
			}}>Sign out</ActionButton>
		</div>
		<div class="card">
			<h2>Your Showdown name</h2>
			{user.showdown ? <>
				<p>Linked to <strong>{user.showdown.name}</strong>. In your leagues, you're this player, and your Draft Planner
					plans are the same ones as <code>/planner</code> on Showdown.</p>
				<ActionButton class="danger" confirm={`Unlink ${user.showdown.name}? You can link it again any time.`} action={async () => {
					await api.delete('/api/me/showdown');
					await refreshMe();
				}}>Unlink</ActionButton>
			</> : <p>Leagues know players by their Showdown names. Link yours so you can play here.</p>}
			{code ? <div class="notice">
				<p>On <a href="https://play.ogreclan.com" target="_blank" rel="noreferrer">play.ogreclan.com</a>, logged in as the
					name you want to link, type this in any chat room:</p>
				<p><code class="code-big">{code.command}</code>{' '}
					<button type="button" class="small" onClick={() => void navigator.clipboard?.writeText(code.command).then(() => toast('Copied.'))}>
						Copy
					</button></p>
				<p class="muted small">The code works once, for <Countdown until={new Date(code.expiresAt).getTime()} />. This page
					updates when it's done.</p>
			</div> : <p>
				<ActionButton class={user.showdown ? '' : 'primary'} action={async () => {
					setCode(await api.post<LinkCode>('/api/me/link-code'));
				}}>{user.showdown ? 'Link a different name' : 'Link my Showdown name'}</ActionButton>
			</p>}
		</div>
	</>;
}
